import { ESPER_BUILTIN_FUNCTIONS, ESPER_KEYWORDS, ESPER_OPERATORS } from '../app/monacoSetup.js'

const SCHEMA_SOURCE = 'schema'
const DYNAMIC_SOURCE = 'dynamic'

const sanitizeSections = (fieldSchema = []) => {
  if (!fieldSchema) {
    return []
  }

  if (Array.isArray(fieldSchema)) {
    return fieldSchema
  }

  return Object.entries(fieldSchema).map(([label, fields]) => ({ label, fields }))
}

const normalizeFieldMeta = (path, meta = {}, category) => {
  const rawType =
    meta?.type ??
    meta?.logicalType ??
    (Array.isArray(meta) ? 'array' : typeof meta === 'string' ? meta : undefined)

  const type = rawType ?? 'object'
  const description = meta.description ?? meta.doc ?? ''

  return {
    path,
    label: path,
    type: Array.isArray(type) ? type.join(' | ') : type,
    description,
    category,
    source: SCHEMA_SOURCE,
    usageFrequency: 0
  }
}

export const flattenFieldSchema = (fieldSchema) => {
  const sections = sanitizeSections(fieldSchema)
  const flattened = []

  sections.forEach((section) => {
    const { label, fields } = section
    if (!fields) {
      return
    }

    Object.entries(fields).forEach(([path, meta]) => {
      flattened.push(normalizeFieldMeta(path, meta, label ?? 'schema'))
    })
  })

  return flattened
}

const collectDynamicFromValue = (value, basePath, accumulator) => {
  if (value === null || value === undefined) {
    accumulator.set(basePath, { usageFrequency: (accumulator.get(basePath)?.usageFrequency ?? 0) + 1, sample: value, type: 'null' })
    return
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const entry = accumulator.get(basePath) ?? { usageFrequency: 0, sample: value, type: typeof value }
    entry.usageFrequency += 1
    if (entry.sample === undefined) {
      entry.sample = value
    }
    if (entry.type !== typeof value) {
      entry.type = `${entry.type}|${typeof value}`
    }
    accumulator.set(basePath, entry)
    return
  }

  if (Array.isArray(value)) {
    const entry = accumulator.get(basePath) ?? { usageFrequency: 0, sample: value.slice(0, 1), type: 'array' }
    entry.usageFrequency += 1
    accumulator.set(basePath, entry)

    value.forEach((item, index) => {
      const nextPath = `${basePath}[${isNaN(index) ? index : '*'}]`
      collectDynamicFromValue(item, nextPath, accumulator)
    })
    return
  }

  if (typeof value === 'object') {
    const entry = accumulator.get(basePath) ?? { usageFrequency: 0, sample: value, type: 'object' }
    entry.usageFrequency += 1
    accumulator.set(basePath, entry)

    Object.entries(value).forEach(([key, nested]) => {
      const nextPath = basePath ? `${basePath}.${key}` : key
      collectDynamicFromValue(nested, nextPath, accumulator)
    })
  }
}

export const extractDynamicFieldStats = (events = []) => {
  const accumulator = new Map()

  events.forEach((event) => {
    if (event && typeof event === 'object') {
      Object.entries(event).forEach(([key, value]) => {
        collectDynamicFromValue(value, key, accumulator)
      })
    }
  })

  return Array.from(accumulator.entries()).map(([path, meta]) => ({
    path,
    label: path,
    type: meta.type,
    description: meta.sample === undefined || meta.sample === null ? `Observed via live event (${meta.type})` : `Example: ${JSON.stringify(meta.sample).slice(0, 120)}`,
    category: 'dynamic',
    source: DYNAMIC_SOURCE,
    usageFrequency: meta.usageFrequency
  }))
}

export const mergeCompletionEntries = (schemaEntries = [], dynamicEntries = []) => {
  const merged = new Map()

  schemaEntries.forEach((entry) => {
    merged.set(entry.path, { ...entry })
  })

  dynamicEntries.forEach((entry) => {
    if (merged.has(entry.path)) {
      const existing = merged.get(entry.path)
      merged.set(entry.path, {
        ...existing,
        usageFrequency: existing.usageFrequency + entry.usageFrequency,
        description: existing.description || entry.description,
        source: existing.source,
        category: existing.category ?? entry.category
      })
    } else {
      merged.set(entry.path, entry)
    }
  })

  return Array.from(merged.values()).sort((a, b) => {
    if (b.usageFrequency !== a.usageFrequency) {
      return b.usageFrequency - a.usageFrequency
    }
    return a.path.localeCompare(b.path)
  })
}

export const buildCompletionCatalog = ({ fieldSchema = [], events = [] } = {}) => {
  const schemaEntries = flattenFieldSchema(fieldSchema)
  const dynamicEntries = extractDynamicFieldStats(events)
  const merged = mergeCompletionEntries(schemaEntries, dynamicEntries)

  return {
    schemaEntries,
    dynamicEntries,
    merged
  }
}

export const createFieldCompletionItems = (entries, monaco, range) =>
  entries.map((entry) => ({
    label: entry.label,
    insertText: entry.path,
    detail: `${entry.type} • ${entry.category}`,
    documentation: entry.description,
    kind: monaco.languages.CompletionItemKind.Field,
    range
  }))

export const createEsperSupportCompletionItems = (monaco, range) => {
  const keywordItems = ESPER_KEYWORDS.map((keyword) => ({
    label: keyword.toUpperCase(),
    insertText: keyword.toUpperCase(),
    kind: monaco.languages.CompletionItemKind.Keyword,
    range
  }))

  const functionItems = ESPER_BUILTIN_FUNCTIONS.map((fn) => ({
    label: `${fn}()` ,
    insertText: `${fn}($0)`,
    kind: monaco.languages.CompletionItemKind.Function,
    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
    range
  }))

  const operatorItems = ESPER_OPERATORS.map((operator) => ({
    label: operator,
    insertText: operator,
    kind: monaco.languages.CompletionItemKind.Operator,
    range
  }))

  return [...keywordItems, ...functionItems, ...operatorItems]
}
